import React, { useState, useEffect, useRef } from "react";
import { Zap, Clock, ArrowRight, Flame, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { useTheme } from "components/Theme/ThemeProvider";
import { summaryApi } from "common";


function FeaturedDealsBanner({ onViewAllDeals }) {
  const { colors } = useTheme();
  const [featuredDeal, setFeaturedDeal] = useState(null);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const bannerRef = useRef(null);
  const sparkleRef = useRef(null);

  useEffect(() => {
    fetchFeaturedDeal();
    animateBanner();
  }, []);

  useEffect(() => {
    if (featuredDeal) {
      calculateTimeLeft();
      const timer = setInterval(calculateTimeLeft, 1000);
      return () => clearInterval(timer);
    }
  }, [featuredDeal]);

  useEffect(() => {
    if (featuredDeal && featuredDeal.products.length > 1) {
      const interval = setInterval(() => {
        setCurrentProductIndex((prev) => (prev + 1) % featuredDeal.products.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [featuredDeal]);

  const animateBanner = () => {
    if (bannerRef.current) {
      gsap.fromTo(
        bannerRef.current,
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
      );
    }

    if (sparkleRef.current) {
      gsap.to(sparkleRef.current, {
        rotation: 360,
        duration: 3,
        repeat: -1,
        ease: "linear"
      });
    }
  };

  const fetchFeaturedDeal = async () => {
    try {
      const response = await fetch(summaryApi.getActiveDeals.url, {
        method: summaryApi.getActiveDeals.method,
        
      });
      const data = await response.json();
      
      if (data.success && data.data.length > 0) {
        // Get the highest priority deal
        const topDeal = data.data.sort((a, b) => b.priority - a.priority)[0];
        setFeaturedDeal(topDeal);
      }
    } catch (error) {
      console.error("Error fetching featured deal:", error);
    }
  };

  const calculateTimeLeft = () => {
    if (!featuredDeal) return;
    
    const difference = new Date(featuredDeal.endDate) - new Date();
    
    if (difference <= 0) {
      setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    setTimeLeft({
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    });
  };

  if (!featuredDeal || !featuredDeal.products.length) {
    return null;
  }

  const currentProduct = featuredDeal.products[currentProductIndex];
  const discountPercentage = Math.round((currentProduct.dealDiscount / currentProduct.productId.productPrice) * 100);

  return (
    <motion.div
      ref={bannerRef}
      className="relative overflow-hidden rounded-3xl p-8 md:p-12"
      style={{
        background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.accent}15 50%, ${colors.error}15 100%)`,
        border: `2px solid ${colors.border}`
      }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-20 -left-20 w-64 h-64 rounded-full opacity-20"
          style={{ backgroundColor: colors.primary }}
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, -50, 0],
            scale: [1.2, 1, 1.2]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full opacity-20"
          style={{ backgroundColor: colors.accent }}
        />
      </div>

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Left Content */}
        <div className="space-y-6">
          {/* Badge */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", duration: 0.8 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm"
            style={{
              background: `linear-gradient(135deg, ${colors.error} 0%, ${colors.accent} 100%)`,
              color: "white"
            }}
          >
            <Flame className="w-5 h-5" />
            {discountPercentage}% OFF - LIMITED TIME!
          </motion.div>

          {/* Title */}
          <div>
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl font-black mb-2"
              style={{ color: colors.text }}
            >
              {featuredDeal.dealName}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg"
              style={{ color: colors.textSecondary }}
            >
              {featuredDeal.dealDescription}
            </motion.p>
          </div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-6 rounded-2xl"
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`
            }}
          >
            <p className="text-sm font-semibold uppercase tracking-wider mb-2" style={{ color: colors.accent }}>
              {currentProduct.productId.productBrand}
            </p>
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.text }}>
              {currentProduct.productId.productName}
            </h3>
            
            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-4xl font-black" style={{ color: colors.primary }}>
                ₦{currentProduct.dealPrice}
              </span>
              <span className="text-2xl line-through" style={{ color: colors.textSecondary }}>
                ₦{currentProduct.productId.productPrice}
              </span>
              <span className="text-lg font-bold" style={{ color: colors.success }}>
                Save ₦{currentProduct.dealDiscount}
              </span>
            </div>

            {/* Stock Info */}
            {currentProduct.stockLimit && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: colors.textSecondary }}>
                    Only {currentProduct.stockLimit - currentProduct.soldCount} left!
                  </span>
                  <span className="font-semibold" style={{ color: colors.error }}>
                    {Math.round((currentProduct.soldCount / currentProduct.stockLimit) * 100)}% sold
                  </span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.border }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(currentProduct.soldCount / currentProduct.stockLimit) * 100}%` }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="h-full"
                    style={{
                      background: `linear-gradient(90deg, ${colors.error} 0%, ${colors.accent} 100%)`
                    }}
                  />
                </div>
              </div>
            )}
          </motion.div>

          {/* Countdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center gap-4"
          >
            <div className="flex items-center gap-2">
              <Clock className="w-6 h-6" style={{ color: colors.error }} />
              <span className="font-semibold" style={{ color: colors.text }}>
                Ends in:
              </span>
            </div>
            <div className="flex gap-2">
              {[
                { label: "H", value: timeLeft.hours },
                { label: "M", value: timeLeft.minutes },
                { label: "S", value: timeLeft.seconds }
              ].map((item, index) => (
                <React.Fragment key={item.label}>
                  <div
                    className="px-3 py-2 rounded-lg min-w-[50px] text-center"
                    style={{
                      backgroundColor: colors.bgSecondary,
                      border: `2px solid ${colors.border}`
                    }}
                  >
                    <p className="text-2xl font-black" style={{ color: colors.primary }}>
                      {String(item.value).padStart(2, "0")}
                    </p>
                    <p className="text-xs" style={{ color: colors.textSecondary }}>
                      {item.label}
                    </p>
                  </div>
                  {index < 2 && <span className="text-2xl" style={{ color: colors.textSecondary }}>:</span>}
                </React.Fragment>
              ))}
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.05, x: 10 }}
            whileTap={{ scale: 0.95 }}
            onClick={onViewAllDeals}
            className="flex items-center gap-3 px-8 py-4 rounded-xl font-bold text-lg text-white shadow-2xl group"
            style={{
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
            }}
          >
            <Zap className="w-6 h-6" />
            View All Flash Deals
            <ArrowRight className="w-6 h-6 transition-transform group-hover:translate-x-2" />
          </motion.button>
        </div>

        {/* Right Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="relative"
        >
          <div className="relative">
            {/* Sparkle Effect */}
            <div ref={sparkleRef} className="absolute -top-6 -right-6 z-20">
              <Sparkles className="w-12 h-12" style={{ color: colors.accent }} />
            </div>

            {/* Product Image */}
            <motion.div
              key={currentProductIndex}
              initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.5 }}
              className="relative rounded-3xl overflow-hidden shadow-2xl"
              style={{ border: `4px solid ${colors.border}` }}
            >
              <img
                src={currentProduct.productId.productImage[0]}
                alt={currentProduct.productId.productName}
                className="w-full h-[400px] object-cover"
              />
              
              {/* Gradient Overlay */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(to top, ${colors.bg}80 0%, transparent 50%)`
                }}
              />
            </motion.div>

            {/* Floating Badge */}
            <motion.div
              animate={{
                y: [0, -10, 0],
                rotate: [0, 5, 0]
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -bottom-4 -left-4 px-6 py-3 rounded-2xl shadow-2xl"
              style={{
                background: `linear-gradient(135deg, ${colors.success} 0%, ${colors.primary} 100%)`,
                border: `3px solid ${colors.bg}`
              }}
            >
              <p className="text-sm font-bold text-white">
                🔥 HOT DEAL
              </p>
            </motion.div>
          </div>

          {/* Product Indicators */}
          {featuredDeal.products.length > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {featuredDeal.products.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => setCurrentProductIndex(index)}
                  whileHover={{ scale: 1.2 }}
                  className="rounded-full transition-all"
                  style={{
                    width: currentProductIndex === index ? "32px" : "10px",
                    height: "10px",
                    backgroundColor: currentProductIndex === index ? colors.primary : colors.border
                  }}
                />
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

export default FeaturedDealsBanner;